import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from '../../services/account.service';
import { InformationMessageService } from '../../services/information-message.service';
import { Subscription } from 'rxjs';
import type { HelpMenuItem } from './config/help-menu.config';
import { HELP_MENU_ITEMS } from './config/help-menu.config';

/** ホバー離脱後のクローズ遅延（斜め移動の取りこぼしを防ぐ） */
const HELP_CLOSE_DELAY_MS = 150;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  userName: string = '';
  private accountSubscription?: Subscription;
  /** ヘルプメニュー遅延クローズ用タイマー */
  private helpCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /** ヘルプメニュー表示制御（PC: hover / スマホ: タップトグル） */
  helpOpen = false;
  /** API の enable が true のときのみヘルプブロックを表示 */
  helpMenuEnabled = true;
  /** ヘルプメニュー項目（API 取得、失敗時は埋め込み） */
  helpMenuItems: HelpMenuItem[] = HELP_MENU_ITEMS;

  constructor(
    private accountService: AccountService,
    private router: Router,
    private informationMessageService: InformationMessageService
  ) {}

  ngOnInit(): void {
    // アカウント情報を購読してユーザー名を更新
    this.accountSubscription = this.accountService.account$.subscribe(
      (account) => {
        if (account) {
          this.userName = this.accountService.userName;
        } else {
          this.userName = '';
        }
      }
    );

    // 初期値の設定
    if (this.accountService.accountInfo) {
      this.userName = this.accountService.userName;
    }

    // ヘルプメニューを API から取得（失敗時は埋め込みを使用）
    this.informationMessageService.getInformationMessage().subscribe((res) => {
      this.helpMenuEnabled = res.enable;
      this.helpMenuItems = res.messages.length > 0 ? res.messages : HELP_MENU_ITEMS;
    });
  }

  ngOnDestroy(): void {
    this.cancelHelpClose();
    if (this.accountSubscription) {
      this.accountSubscription.unsubscribe();
    }
  }

  /**
   * ログアウト処理
   */
  onLogout(): void {
    this.accountService.logout();
  }

  /**
   * トップページに遷移
   */
  navigateToTop(): void {
    this.router.navigate(['/inventory/top']);
  }

  /** 遅延クローズをスケジュール（斜め移動中は再ホバーでキャンセルされる） */
  private scheduleHelpClose(): void {
    this.cancelHelpClose();
    this.helpCloseTimer = setTimeout(() => {
      this.helpOpen = false;
      this.helpCloseTimer = null;
    }, HELP_CLOSE_DELAY_MS);
  }

  /** 遅延クローズをキャンセル */
  private cancelHelpClose(): void {
    if (this.helpCloseTimer != null) {
      clearTimeout(this.helpCloseTimer);
      this.helpCloseTimer = null;
    }
  }

  /** PC: ヘルプメニューにホバーで表示 */
  onHelpMouseEnter(): void {
    this.cancelHelpClose();
    this.helpOpen = true;
  }

  /** PC: ヘルプメニューから離れたら遅延して非表示（斜め移動に耐える） */
  onHelpMouseLeave(): void {
    this.scheduleHelpClose();
  }

  /** スマホ: タップで開閉トグル */
  toggleHelp(): void {
    this.helpOpen = !this.helpOpen;
  }

  /**
   * メニュー項目クリック: 外部リンクを安全に新規タブで開き、ドロップダウンを閉じる
   */
  onHelpItemClick(item: HelpMenuItem): void {
    if (item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    }
    this.helpOpen = false;
  }

  /** メニュー外クリックで閉じる（主にスマホ）。即時クローズのため遅延タイマーはキャンセル */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.helpOpen && !target.closest('.help-menu-wrapper')) {
      this.cancelHelpClose();
      this.helpOpen = false;
    }
  }
}
